/* The salon's own people, for the owner who manages them.
 *
 * Every decision this makes is in js/rules/members.js, pure and tested. What is
 * here is the wiring, and it is split down one line — the same line the admin
 * app's members page is split down, and for the same reason:
 *
 *   list, create   the `tenant-members` edge function. Both need auth.users,
 *                  which `authenticated` cannot reach at any grant: one to put
 *                  an email beside a user id, the other to make an account.
 *   role, remove   ordinary REST under the owner policies in
 *                  server/schema/09-owner-members.sql. The row is already in
 *                  hand, and a REST call under a policy is easier to reason
 *                  about than an RPC that reimplements one.
 *
 * NOT in the write queue, deliberately. Everything the board does goes through
 * sync.enqueue so a shift survives the wifi dropping, because a turn that
 * happened is a fact whenever it reaches the server. An account is not like
 * that: creating one offline would hand somebody a password for an account that
 * does not exist yet, and removing one would report an access change that has
 * not happened. These are online-only, they say so, and they fail loudly.
 */
import * as Vue from 'vue';
import { canManage, roleOf } from './rules/members.js';
import { sync } from './sync.js';

var FUNCTION = 'tenant-members';

var state = Vue.reactive({
  rows: [],
  seats: null,       // null is both "unlimited" and "unknown" — see the rules
  loading: false,
  busy: false,
  error: null,
  // The password from the last account created, shown once and never fetched
  // again. Cleared by the screen as soon as it is dismissed.
  issued: null
});

/* What this session may do here.
 *
 * Read off the loaded list rather than asked of the server separately, so the
 * screen and its buttons cannot disagree: whatever the list says about you is
 * what the controls obey. Until the list has loaded this is false, which is the
 * right default — the tab stays out of the way rather than flickering in.
 *
 * It is not the enforcement. `private.is_tenant_owner()` decides, in the
 * database, on every write.
 */
var youAreOwner = Vue.computed(function () {
  var me = sync.auth.user && sync.auth.user.id;
  return canManage(roleOf(state.rows, me));
});

async function invoke(action, body) {
  var res = await sync.sb.functions.invoke(FUNCTION, {
    body: Object.assign({ action: action }, body || {})
  });
  // supabase-js reports a non-2xx as an error with the body unread, so the
  // sentence the function wrote — which is the useful half — has to be dug out
  // of the response rather than taken from err.message ("Edge Function returned
  // a non-2xx status code", which tells a salon owner nothing).
  if (res.error) {
    var said = null;
    try {
      var ctx = res.error.context;
      if (ctx && typeof ctx.json === 'function') said = (await ctx.json()).error;
    } catch (e) { /* not json, or already consumed */ }
    throw new Error(said || res.error.message || 'That did not work.');
  }
  return res.data;
}

async function load() {
  if (!sync.auth.user) return;
  state.loading = true;
  state.error = null;
  try {
    var data = await invoke('members.list');
    state.rows = data.members || [];
    state.seats = data.seats == null ? null : data.seats;
  } catch (err) {
    /* A station gets 403 here, which is not a failure — it is the answer, and
     * the screen it would be shown on is one they never see. Reporting it would
     * put an error on the console of every front desk in the product. */
    state.rows = [];
    state.error = /owner/i.test(String(err.message)) ? null : String(err.message);
  } finally {
    state.loading = false;
  }
}

async function create(email, role) {
  state.busy = true;
  state.error = null;
  try {
    var data = await invoke('members.create', { email: email, role: role });
    state.issued = { email: data.member.email, password: data.password };
    await load();
    return true;
  } catch (err) {
    state.error = String(err.message);
    return false;
  } finally {
    state.busy = false;
  }
}

// A role change and a removal are plain REST. The owner policies scope both to
// this salon, and the last-owner trigger refuses either if it would leave the
// place without one — which is why the error is surfaced verbatim: that message
// is written for a person to read.
async function setRole(userId, role) {
  state.busy = true;
  state.error = null;
  try {
    var res = await sync.sb.from('tenant_members')
      .update({ role: role })
      .eq('tenant_id', sync.auth.tenantId)
      .eq('user_id', userId);
    if (res.error) throw new Error(res.error.message);
    await load();
    return true;
  } catch (err) {
    state.error = String(err.message);
    return false;
  } finally {
    state.busy = false;
  }
}

async function remove(userId) {
  state.busy = true;
  state.error = null;
  try {
    var res = await sync.sb.from('tenant_members')
      .delete()
      .eq('tenant_id', sync.auth.tenantId)
      .eq('user_id', userId);
    if (res.error) throw new Error(res.error.message);
    await load();
    return true;
  } catch (err) {
    state.error = String(err.message);
    return false;
  } finally {
    state.busy = false;
  }
}

function forget() { state.issued = null; }

/* Loaded on sign-in, and again when the tenant resolves.
 *
 * The tenant lands a round trip after the session does — see the watcher in
 * store.js that says so about the realtime channels — and while it is unknown
 * the REST calls above have no tenant to scope by. Asking again when it arrives
 * is what makes this right on a station's first sign-in and on one that another
 * salon has used.
 */
Vue.watch(function () {
  return [sync.auth.user && sync.auth.user.id, sync.auth.tenantId];
}, function () {
  if (sync.auth.user) load(); else state.rows = [];
}, { immediate: true });

export const members = {
  state: state,
  youAreOwner: youAreOwner,
  load: load,
  create: create,
  setRole: setRole,
  remove: remove,
  forget: forget
};
